import { useState } from 'react';
import { router, Link } from '@inertiajs/react';
import GmLayout from '@/layouts/GmLayout';
import { Editor } from '@/components/Editor';

/**
 * Book with chapters for the dropdown.
 */
interface TomeBook {
    id: number;
    title: string;
    chapters: { id: number; title: string }[];
}

/**
 * Props for the Create page.
 */
interface Props {
    books: TomeBook[];
    preselectedChapterId?: string | null;
}

/**
 * GM Tome Create page - form to create a new tome page.
 */
export default function Create({ books, preselectedChapterId }: Props) {
    // Form fields
    const [name, setName] = useState('');
    const [content, setContent] = useState('');
    const [chapterId, setChapterId] = useState(preselectedChapterId || '');
    const [isPublished, setIsPublished] = useState(false);

    // Form state
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});

    /**
     * Handle form submission.
     */
    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        setErrors({});

        router.post('/gm/tome', {
            name,
            content: content || null,
            chapter_id: chapterId,
            is_published: isPublished,
        }, {
            onError: (errs) => {
                setErrors(errs);
                setIsSubmitting(false);
            },
            onSuccess: () => setIsSubmitting(false),
        });
    };

    return (
        <GmLayout title="Create Tome Page" activePath="/gm/tome">
            <div className="max-w-4xl">
                <div className="flex items-center justify-between mb-6">
                    <h1 className="text-2xl font-bold text-white">📖 Create Tome Page</h1>
                    <Link href="/gm/tome" className="text-gray-400 hover:text-white transition-colors">
                        ← Back to Tome
                    </Link>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Basic Info Section */}
                    <div className="bg-slate-800 border border-slate-700 rounded-lg p-6 space-y-4">
                        <h2 className="text-lg font-semibold text-white mb-4">Page Information</h2>

                        {/* Name */}
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-1">
                                Page Title <span className="text-red-400">*</span>
                            </label>
                            <input
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white"
                                placeholder="The History of the Realm"
                                required
                            />
                            {errors.name && <p className="mt-1 text-sm text-red-400">{errors.name}</p>}
                        </div>

                        {/* Chapter Selection */}
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-1">
                                Chapter <span className="text-red-400">*</span>
                            </label>
                            <select
                                value={chapterId}
                                onChange={(e) => setChapterId(e.target.value)}
                                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white"
                                required
                            >
                                <option value="">Select a chapter...</option>
                                {books.map((book) => (
                                    <optgroup key={book.id} label={book.title}>
                                        {book.chapters.map((chapter) => (
                                            <option key={chapter.id} value={chapter.id}>
                                                {chapter.title}
                                            </option>
                                        ))}
                                    </optgroup>
                                ))}
                            </select>
                            {errors.chapter_id && <p className="mt-1 text-sm text-red-400">{errors.chapter_id}</p>}
                        </div>

                        {/* Content - Markdown Editor */}
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-1">Content</label>
                            <p className="text-xs text-gray-500 mb-2">
                                Use Markdown to format your content. Images can be added after creating the page.
                            </p>
                            <div data-color-mode="dark">
                                <Editor
                                    value={content}
                                    onChange={(value) => setContent(value)}
                                    placeholder="Write your lore content here..."
                                    height={400}
                                />
                            </div>
                            {errors.content && <p className="mt-1 text-sm text-red-400">{errors.content}</p>}
                        </div>

                        {/* Published Status */}
                        <div className="flex items-center">
                            <label className="flex items-center gap-2 text-sm text-gray-300">
                                <input
                                    type="checkbox"
                                    checked={isPublished}
                                    onChange={(e) => setIsPublished(e.target.checked)}
                                    className="rounded bg-slate-700 border-slate-600 text-purple-600"
                                />
                                Publish immediately (visible to all users)
                            </label>
                        </div>
                        <p className="text-xs text-gray-500">
                            Draft pages are only visible to GMs. You can publish later from the edit page.
                        </p>
                    </div>

                    {/* Submit Buttons */}
                    <div className="flex gap-4">
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded transition-colors disabled:opacity-50"
                        >
                            {isSubmitting ? 'Creating...' : 'Create Page'}
                        </button>
                        <Link
                            href="/gm/tome"
                            className="px-6 py-2 bg-slate-600 hover:bg-slate-500 text-white rounded transition-colors"
                        >
                            Cancel
                        </Link>
                    </div>
                </form>
            </div>
        </GmLayout>
    );
}

